import { useState, useEffect } from 'react';
import { Wordmark, Icon } from './Shared';
import { useCart } from '../../lib/useCart';
import { formatMoney } from '../../lib/shopify';

export function Header({ currentPage, goHome, goCollections, openCart, onLogoPartClick, menuOpen, setMenuOpen }) {
  const [scrolled, setScrolled] = useState(false);
  const { count } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className={`site-header ${scrolled ? 'scrolled' : ''}`} data-testid="site-header">
      <div className="header-inner">
        <nav className="nav-left">
          <a className="nav-link" onClick={goCollections} data-testid="nav-shop">Shop</a>
          <a className="nav-link" onClick={goCollections}>Offers</a>
          <a className="nav-link new" onClick={goCollections}>New</a>
          <a className="nav-link" onClick={goHome}>What's Inside</a>
        </nav>
        <div className="wordmark-link" data-testid="logo-home">
          <Wordmark height={scrolled ? 32 : 42} interactive onPartClick={onLogoPartClick} />
        </div>
        <div className="nav-right">
          <button className="icon-btn" aria-label="Search" data-testid="search-btn">
            <Icon kind="search" size={22} color="#1E2B8C" />
          </button>
          <button className="icon-btn" aria-label="Cart" onClick={openCart} data-testid="open-cart-btn">
            <Icon kind="cart" size={22} color="#1E2B8C" />
            {count > 0 && <span className="cart-count" data-testid="cart-count">{count}</span>}
          </button>
          <button
            className="hamburger"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Menu"
            data-testid="hamburger-btn"
          >
            <Icon kind={menuOpen ? 'x' : 'menu'} size={22} color="#1E2B8C" />
          </button>
        </div>
      </div>
      {menuOpen && (
        <div className="mobile-menu" data-testid="mobile-menu">
          <a className="nav-link" onClick={() => { goCollections(); setMenuOpen(false); }}>Shop</a>
          <a className="nav-link" onClick={() => { goCollections(); setMenuOpen(false); }}>Offers</a>
          <a className="nav-link new" onClick={() => { goCollections(); setMenuOpen(false); }}>New</a>
          <a className="nav-link" onClick={() => { goHome(); setMenuOpen(false); }}>What's Inside</a>
        </div>
      )}
    </header>
  );
}

export function CartDrawer({ open, onClose }) {
  const { cart, updateLine, removeLine } = useCart();

  return (
    <>
      <div
        className={`cart-backdrop ${open ? 'open' : ''}`}
        onClick={onClose}
        data-testid="cart-backdrop"
      />
      <aside
        className={`cart-drawer ${open ? 'open' : ''}`}
        role="dialog"
        aria-label="Cart"
        data-testid="cart-drawer"
      >
        <div className="cart-header">
          <h3>Your Bowl</h3>
          <button className="cart-close" onClick={onClose} aria-label="Close" data-testid="cart-close">
            <Icon kind="x" size={22} color="#1E2B8C" />
          </button>
        </div>
        <div className="cart-body">
          {!cart || cart.lines.length === 0 ? (
            <div className="cart-empty" data-testid="cart-empty">
              <div className="big">Nothing here yet.</div>
              <div>Add something honest to the bowl.</div>
            </div>
          ) : cart.lines.map((it) => (
            <div className="cart-item" key={it.id} data-testid={`cart-item-${it.id}`}>
              <div className="img">
                {it.image ? (
                  <img src={it.image} alt={it.productTitle} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <div className="placeholder" style={{ height: '100%' }}>
                    <span>{it.productTitle.split(' ')[0]}</span>
                  </div>
                )}
              </div>
              <div>
                <h4 className="name">{it.productTitle}</h4>
                <div className="meta">{it.productType || it.variantTitle}</div>
                <div className="qty">
                  <button
                    onClick={() => updateLine(it.id, Math.max(1, it.quantity - 1))}
                    data-testid={`cart-decrease-${it.id}`}
                  >−</button>
                  <span>{it.quantity}</span>
                  <button
                    onClick={() => updateLine(it.id, it.quantity + 1)}
                    data-testid={`cart-increase-${it.id}`}
                  >+</button>
                </div>
                <div
                  className="remove"
                  onClick={() => removeLine(it.id)}
                  data-testid={`cart-remove-${it.id}`}
                >Remove</div>
              </div>
              <div className="price">{formatMoney(it.price * it.quantity, cart.currency)}</div>
            </div>
          ))}
        </div>
        {cart && cart.lines.length > 0 && (
          <div className="cart-footer">
            <div className="cart-total">
              <span>Subtotal</span>
              <span data-testid="cart-subtotal">{formatMoney(cart.subtotal, cart.currency)}</span>
            </div>
            <a
              href={cart.checkoutUrl}
              className="imp-btn"
              style={{ width: '100%', justifyContent: 'center' }}
              data-testid="checkout-btn"
              target="_self"
            >Checkout</a>
          </div>
        )}
      </aside>
    </>
  );
}

export function BottomTabs({ currentPage, goHome, goCollections, openCart, cartCount, menuOpen, setMenuOpen }) {
  const tabs = [
    { id: 'home', label: 'Home', icon: 'home', onClick: goHome, active: currentPage === 'home' && !menuOpen },
    { id: 'shop', label: 'Shop', icon: 'grid', onClick: goCollections, active: currentPage === 'collections' && !menuOpen },
    { id: 'search', label: 'Search', icon: 'search', onClick: () => {}, active: false },
    { id: 'bag', label: 'Bag', icon: 'cart', onClick: openCart, active: false, badge: cartCount },
    { id: 'menu', label: 'Menu', icon: menuOpen ? 'x' : 'menu', onClick: () => setMenuOpen((o) => !o), active: menuOpen },
  ];
  return (
    <nav className="bottom-tabs" data-testid="bottom-tabs" aria-label="Bottom navigation">
      {tabs.map((t) => (
        <button
          key={t.id}
          className={`bottom-tab ${t.active ? 'active' : ''}`}
          onClick={t.onClick}
          data-testid={`bottom-tab-${t.id}`}
          aria-label={t.label}
        >
          <div className="bottom-tab-icon">
            <Icon kind={t.icon} size={22} color="currentColor" />
            {t.badge > 0 && <span className="bottom-tab-badge" data-testid="bottom-tab-badge">{t.badge}</span>}
          </div>
          <span className="bottom-tab-label">{t.label}</span>
        </button>
      ))}
    </nav>
  );
}
